import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { JournalEntry, Fragrance, Ingredient } from '../App';
import { IconHeart, IconPlus, IconChevron, IconX } from './Icons';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface WishlistProps {
  userId: string;
  authToken: string;
  wishlistIds: Set<string>;
  journalEntries: JournalEntry[];
  catalog: Fragrance[];
  ingredients: Ingredient[];
  recommendedIds: Set<string>;
  challengeId: string | null;
  onWishlistToggle: (fragranceId: string, fragranceName: string, fragranceHouse: string) => void;
  onAddToJournal: (fragrance: any) => void;
}

interface WishlistItem {
  fragranceId: string;
  fragranceName: string;
  fragranceHouse: string;
  addedAt: string;
  fragrance?: {
    description?: string;
    topNotes?: string[];
    middleNotes?: string[];
    baseNotes?: string[];
    notes?: string[];
    year?: number;
  };
}

const TAG_STYLES: Record<string, string> = {
  recommended: 'bg-stone text-cream',
  challenge:   'bg-charcoal text-cream',
  sample:      'bg-cream-dark text-stone',
  full_bottle: 'bg-cream-dark text-stone',
};

const TAG_LABELS: Record<string, string> = {
  recommended: 'Recommended',
  challenge:   'Challenge',
  sample:      'Sample',
  full_bottle: 'Full Bottle',
};

const FILTER_OPTIONS = ['all', 'recommended', 'challenge', 'sample', 'full_bottle'];
const FILTER_LABELS: Record<string, string> = {
  all: 'All',
  recommended: 'Recommended',
  challenge: 'Challenge',
  sample: 'Sample',
  full_bottle: 'Full Bottle',
};

const Wishlist: React.FC<WishlistProps> = ({
  userId,
  authToken,
  wishlistIds,
  journalEntries,
  catalog,
  ingredients,
  recommendedIds,
  challengeId,
  onWishlistToggle,
  onAddToJournal,
}) => {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [ingredientPopup, setIngredientPopup] = useState<Ingredient | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => {
    fetchWishlist();
  }, [wishlistIds]);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wishlist`, {
        params: { userId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setItems(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch wishlist:', e);
    }
    setLoading(false);
  };

  const formatDate = (isoString: string) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  };

  const getItemTags = (fragranceId: string): string[] => {
    const tags: string[] = [];
    const journalEntry = journalEntries.find(e => e.fragranceId === fragranceId);
    if (journalEntry?.ownershipStatus === 'full_bottle') tags.push('full_bottle');
    else if (journalEntry?.ownershipStatus === 'sample') tags.push('sample');
    if (challengeId === fragranceId) tags.push('challenge');
    else if (recommendedIds.has(fragranceId)) tags.push('recommended');
    return tags;
  };

  const lookupIngredient = (noteName: string): Ingredient | undefined => {
    const base = noteName.trim();
    const id = base.toLowerCase().replace(/\s+/g, '-');
    const ALIASES: Record<string, string> = {
      'vetivert': 'vetiver', 'vetyver': 'vetiver', 'vetiveryl-acetate': 'vetiver',
      'red-rose': 'rose', 'damascena-rose': 'rose', 'green-rose': 'rose',
      'white-jasmine': 'jasmine', 'jasmine-absolute': 'jasmine',
      'ammber': 'amber', 'ambregris': 'ambergris', 'dark-amber': 'amber',
      'atlas-cedarwood': 'cedar', 'cedar-leaf': 'cedar', 'cedrus': 'cedar',
      'oakwood': 'oak', 'seawater': 'sea-notes', 'sea-water': 'sea-notes',
      'marine-notes': 'sea-notes', 'aquatic': 'sea-notes',
      'white-musks': 'musk', 'musks': 'musk', 'solar-musk': 'musk',
      'vanille': 'vanilla', 'bergamot-oil': 'bergamot',
      'orange-flower': 'neroli', 'pink-grapefruit': 'grapefruit',
      'lemon-leaves': 'lemon', 'italian-lemon': 'lemon',
      'white-pepper': 'pepper', 'earthy': 'vetiver', 'woods': 'wood', 'woody': 'wood',
      'petit-grain': 'petitgrain', 'agar-wood': 'agarwood',
    };
    const lookupId = ALIASES[id] || id;
    return ingredients.find(ing =>
      ing.ingredientId === lookupId ||
      ing.ingredientId === id ||
      ing.name.toLowerCase() === base.toLowerCase()
    );
  };

  const enrichWithCatalog = (item: WishlistItem): WishlistItem => {
    const catalogFrag = catalog.find(f => f.fragranceId === item.fragranceId);
    if (!catalogFrag) return item;
    return {
      ...item,
      fragrance: {
        description: catalogFrag.description,
        topNotes: catalogFrag.topNotes,
        middleNotes: catalogFrag.middleNotes,
        baseNotes: catalogFrag.baseNotes,
        notes: catalogFrag.notes,
        year: catalogFrag.year,
        ...(item.fragrance || {}),
      },
    };
  };

  const filteredItems = items.filter(rawItem => {
    if (activeFilter === 'all') return true;
    return getItemTags(rawItem.fragranceId).includes(activeFilter);
  });

  const tagCounts = FILTER_OPTIONS.reduce((acc, f) => {
    if (f === 'all') { acc[f] = items.length; return acc; }
    acc[f] = items.filter(i => getItemTags(i.fragranceId).includes(f)).length;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-stone">Loading wishlist...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-16 px-6">
        <p className="font-serif text-2xl text-charcoal mb-4">Your wishlist is empty.</p>
        <p className="text-stone text-sm">Tap the ♡ on any fragrance in the Catalog to save it here.</p>
      </div>
    );
  }

  return (
    <>
    {/* Filter tabs */}
    <div className="flex gap-2 flex-wrap mb-4">
      {FILTER_OPTIONS.filter(f => f === 'all' || tagCounts[f] > 0).map(f => (
        <button
          key={f}
          onClick={() => setActiveFilter(f)}
          className={`text-xs tracking-widest uppercase px-3 py-1.5 border transition-colors ${
            activeFilter === f
              ? 'bg-charcoal text-cream border-charcoal'
              : 'bg-white text-stone border-cream-dark hover:border-stone'
          }`}
        >
          {FILTER_LABELS[f]}
          {tagCounts[f] > 0 && f !== 'all' && (
            <span className="ml-1 opacity-60">{tagCounts[f]}</span>
          )}
        </button>
      ))}
    </div>

    <div className="space-y-2">
      {filteredItems.map(rawItem => {
        const item = enrichWithCatalog(rawItem);
        const isExpanded = expanded === item.fragranceId;
        const frag = item.fragrance;
        const itemTags = getItemTags(item.fragranceId);
        const hasDetails = frag && (
          frag.description ||
          (frag.topNotes && frag.topNotes.length > 0) ||
          (frag.middleNotes && frag.middleNotes.length > 0) ||
          (frag.baseNotes && frag.baseNotes.length > 0)
        );

        return (
          <div key={item.fragranceId} className="bg-white border border-cream-dark rounded-xl">
            <div
              className="p-5 flex justify-between items-start cursor-pointer"
              onClick={() => setExpanded(isExpanded ? null : item.fragranceId)}
            >
              <div className="text-left flex-1">
                <p className="font-serif text-lg text-charcoal">{item.fragranceName}</p>
                <p className="text-stone-light text-sm">{item.fragranceHouse}</p>
                <p className="text-stone-light text-xs mt-1">Added {formatDate(item.addedAt)}</p>
                {itemTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {itemTags.map(tag => (
                      <span
                        key={tag}
                        className={`text-xs tracking-widest uppercase px-2 py-0.5 rounded-md ${TAG_STYLES[tag] || 'bg-cream-dark text-stone'}`}
                      >
                        {TAG_LABELS[tag] || tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 ml-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => onAddToJournal({
                    fragranceId: item.fragranceId,
                    name: item.fragranceName,
                    house: item.fragranceHouse,
                    ...frag,
                  })}
                  className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                  title="Log in journal"
                >
                  <IconHeart size={13} className="text-stone-light" />
                </button>
                <button
                  onClick={() => onWishlistToggle(item.fragranceId, item.fragranceName, item.fragranceHouse)}
                  className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                  title="Remove from wishlist"
                >
                  <IconPlus size={13} className="text-charcoal" />
                </button>
                <button
                  onClick={() => setExpanded(isExpanded ? null : item.fragranceId)}
                  className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <IconChevron size={11} direction={isExpanded ? 'up' : 'down'} className="text-stone-light" />
                </button>
              </div>
            </div>

            {isExpanded && frag && (
              <div className="px-5 pb-5 border-t border-cream-dark pt-4 space-y-3">
                {frag.description && (
                  <p className="text-stone text-sm leading-relaxed">{frag.description}</p>
                )}
                {frag.topNotes && frag.topNotes.length > 0 && (
                  <div>
                    <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Top notes</p>
                    <div className="flex flex-wrap gap-2">
                      {frag.topNotes.map((n, i) => {
                        const ing = lookupIngredient(n);
                        return (
                          <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                            className={`text-xs bg-cream-dark text-stone px-3 py-1 rounded-lg ${ing ? 'hover:bg-stone hover:text-cream transition-colors' : 'cursor-default'}`}>
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {frag.middleNotes && frag.middleNotes.length > 0 && (
                  <div>
                    <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Heart notes</p>
                    <div className="flex flex-wrap gap-2">
                      {frag.middleNotes.map((n, i) => {
                        const ing = lookupIngredient(n);
                        return (
                          <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                            className={`text-xs bg-cream-dark text-stone px-3 py-1 rounded-lg ${ing ? 'hover:bg-stone hover:text-cream transition-colors' : 'cursor-default'}`}>
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {frag.baseNotes && frag.baseNotes.length > 0 && (
                  <div>
                    <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Base notes</p>
                    <div className="flex flex-wrap gap-2">
                      {frag.baseNotes.map((n, i) => {
                        const ing = lookupIngredient(n);
                        return (
                          <button key={i} onClick={() => ing && setIngredientPopup(ing)}
                            className={`text-xs bg-cream-dark text-stone px-3 py-1 rounded-lg ${ing ? 'hover:bg-stone hover:text-cream transition-colors' : 'cursor-default'}`}>
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>

    {/* Ingredient popup */}
    {ingredientPopup && (
      <>
        <div className="fixed inset-0 bg-charcoal bg-opacity-40 z-40" onClick={() => setIngredientPopup(null)} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cream z-50 w-80 rounded-xl max-w-[90vw] p-6 shadow-lg">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="font-serif text-xl text-charcoal">{ingredientPopup.name}</p>
              <p className="text-xs tracking-widest uppercase text-stone-light">{ingredientPopup.category}</p>
            </div>
            <button onClick={() => setIngredientPopup(null)} className="text-stone-light hover:text-charcoal ml-4 flex-shrink-0"><IconX size={16} /></button>
          </div>
          <p className="text-stone text-sm leading-relaxed">{ingredientPopup.description}</p>
        </div>
      </>
    )}
    </>
  );
};

export default Wishlist;
